/**
 * Relevés bancaires (M07) : correspondance proposée depuis les en-têtes d'un
 * export de banque, et libellés des propositions.
 */

export type ChampDeReleve = "DATE" | "DATE_DE_VALEUR" | "LIBELLE" | "REFERENCE" | "DEBIT" | "CREDIT" | "MONTANT";

export type AffectationDeReleve = Record<number, ChampDeReleve | undefined>;

const replier = (texte: string) =>
  texte
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/** L'ordre compte : « date de valeur » est une date de valeur avant d'être une date. */
const INDICES: [ChampDeReleve, RegExp][] = [
  ["DATE_DE_VALEUR", /\b(valeur|date val|dt val)\b/],
  ["DATE", /\b(date|jour|operation)\b/],
  ["DEBIT", /\b(debit|debits|sortie|retrait)\b/],
  ["CREDIT", /\b(credit|credits|entree|versement)\b/],
  ["MONTANT", /\b(montant|amount|somme)\b/],
  ["REFERENCE", /\b(reference|ref|piece|cheque)\b/],
  ["LIBELLE", /\b(libelle|intitule|description|designation|motif|label)\b/],
];

export function proposerAffectationDeReleve(entetes: string[]): AffectationDeReleve {
  const affectation: AffectationDeReleve = {};
  const pris = new Set<ChampDeReleve>();
  entetes.forEach((entete, rang) => {
    const repli = replier(entete);
    if (repli === "") return;
    const trouve = INDICES.find(([champ, motif]) => !pris.has(champ) && motif.test(repli));
    if (trouve != null) {
      affectation[rang] = trouve[0];
      pris.add(trouve[0]);
    }
  });
  return affectation;
}

export function colonnesDuReleve(affectation: AffectationDeReleve): Partial<Record<ChampDeReleve, number>> {
  const colonnes: Partial<Record<ChampDeReleve, number>> = {};
  for (const [rang, champ] of Object.entries(affectation)) {
    if (champ != null) colonnes[champ] = Number(rang);
  }
  return colonnes;
}

export function affectationDuReleve(colonnes: Partial<Record<ChampDeReleve, number>>): AffectationDeReleve {
  const affectation: AffectationDeReleve = {};
  for (const [champ, rang] of Object.entries(colonnes)) {
    if (rang != null) affectation[rang] = champ as ChampDeReleve;
  }
  return affectation;
}

/** Ce qui manque pour que la correspondance tienne ; vide, on peut importer. */
export function manquesDuReleve(affectation: AffectationDeReleve): string[] {
  const champs = new Set(Object.values(affectation).filter((c): c is ChampDeReleve => c != null));
  const liste: string[] = [];
  if (!champs.has("DATE")) liste.push("la date d'opération");
  if (!champs.has("LIBELLE")) liste.push("le libellé");
  const debitCredit = champs.has("DEBIT") || champs.has("CREDIT");
  if (debitCredit === champs.has("MONTANT")) liste.push("les montants : débit et crédit, ou un montant signé");
  return liste;
}
